import { useCallback, useMemo } from "react";
import {
  mappingWarnings,
  marketingDataParams,
  Step,
  termsAndConditionsProperties
} from "./FormBondApp.constants";
import { RecoveryData } from "./FormBondApp.types";
// @ts-ignore
const scriptURLString = document?.getElementById("bond-form-script")?.src;
const partnerId = scriptURLString
  ? new URL(scriptURLString).searchParams.get("partnerId") ??
    (process.env.REACT_APP_PARTNER_ID || "")
  : process.env.REACT_APP_PARTNER_ID || "";

const URLStr = process.env.REACT_APP_BASE_URL;

const Url = `${URLStr}/api/v1`.replace("{{ID}}", partnerId);

export const getMarketingData = (searchParams: any) => {
  let obj: any = {};

  marketingDataParams.forEach(item => {
    obj[item] =
      searchParams.get(item) || window.localStorage.getItem(item) || null;
  });

  return obj;
};

export const dataChecker = (data: any) =>
  data &&
  ((typeof data === "object" &&
    !Array.isArray(data) &&
    Object.keys(data).length) ||
    (Array.isArray(data) && Object.keys(data[0]).length))
    ? data
    : null;

export const getPdfUrl = ({
  bondId,
  pdfId,
  bondAccessKey
}: {
  bondId?: string;
  pdfId?: string | null;
  bondAccessKey: string;
}) => {
  const pdfFileLink = `${Url}/bond-records/${bondId}/files/public/${pdfId}?access_key=${bondAccessKey}&format=pdf`;

  return pdfFileLink;
};

export const serializeRecoveryData = ({
  business_dba,
  business_fein_number,
  business_name,
  business_type_id,
  email,
  mailing_city,
  mailing_state,
  mailing_street,
  mailing_zip,
  middle_name,
  fax,
  first_name,
  last_name,
  suffix,
  phone
}: RecoveryData) => {
  const sameData = {
    email,
    mailing_city,
    mailing_state,
    mailing_street,
    mailing_zip,
    phone
  };
  if (business_name) {
    return deleteNullValues({
      ...sameData,
      is_business: true,
      business_dba,
      business_fein_number,
      business_name,
      business_type_id,
      fax,
      applicant_first_name: first_name,
      applicant_middle_name: middle_name,
      applicant_last_name: last_name,
      applicant_suffix: suffix
    });
  }
  return deleteNullValues({
    ...sameData,
    is_business: false,
    first_name,
    last_name,
    middle_name,
    suffix
  });
};

const deleteNullValues = (obj: { [key: string]: string | boolean | null }) => {
  Object.keys(obj).forEach((key: string) => {
    if (obj[key] === null) {
      delete obj[key];
    }
  });
  return obj;
};

export const getSchema = (
  activeSchema: any,
  schema: any,
  step: any,
  context: any,
  mailingState: keyof typeof mappingWarnings
) => {
  const currentSchemaId = activeSchema[step];
  if (!context?.state?.[currentSchemaId]) {
    return;
  }

  const currentSchema = JSON.parse(
    //@ts-ignore
    JSON.stringify(context?.state?.[currentSchemaId])
  ) as any;

  let currentSchemaValue: any = {};
  if (!currentSchema?.properties) {
    return currentSchema;
  }

  if (
    step === Step.BOND_APPLICANT ||
    step === Step.BOND_BUSINESS ||
    step === Step.BOND_BUSINESS
  ) {
    const basicSchema = JSON.parse(
      //@ts-ignore
      JSON.stringify(context?.state?.bondBasicFormSchema)
    ) as any;

    currentSchema.properties = {
      ...basicSchema.properties,
      ...currentSchema.properties
      // ...additionalFields.schema.properties,
    };

    const requiredFields = new Set([
      ...basicSchema.required,
      ...currentSchema.required
    ]);

    currentSchema.required = Array.from(requiredFields);
  }

  delete currentSchema.updated_at;
  delete currentSchema.created_at;
  delete currentSchema.descr;
  delete currentSchema.id;

  if (schema?.[step]?.length) {
    schema?.[step].forEach((key: any) => {
      currentSchemaValue[key] = currentSchema.properties[key];
    });
    currentSchema.properties = {
      ...currentSchemaValue
    };
    currentSchema.required = currentSchema.required.filter((item: any) =>
      schema?.[step].includes(item)
    );
  }
  if (step === Step.START) {
    currentSchema.required = [...currentSchema.required, "is_business"];
  }

  if (Array.isArray(currentSchema?.properties)) {
    currentSchema.properties = {};
  }

  // if (Step.BOND_AGENCY_QUESTIONS === step) return currentSchema;

  Object.entries(currentSchema.properties).forEach(([key, item]: any) => {
    if (!item) return;
    item?.enum === null && delete item.enum;
    item?.enumNames === null && delete item.enumNames;
    if (Step.BOND_AGENCY_QUESTIONS !== step) {
      item.format = item?.format === "" ? undefined : item?.format;
    }
    item.type = Array.isArray(item.type) ? item.type[0] : item.type;
    item.pattern =
      item.pattern ===
      "^((?!000)[0-9\\_]{3}(-?)(?!00)[0-9\\_]{2}\\2(?!0000)[0-9\\_]{4})*$"
        ? undefined
        : item.pattern;
    if (item && Object.keys(item).length > 1) {
      currentSchema.properties[key] = item;
    }
    if (key.includes("email")) {
      currentSchema.properties[key].format = "email address";
    }

    if (key.includes("phone")) {
      currentSchema.properties[key].format = "phone us";
    }
  });

  const keys = Object.keys(currentSchema.properties);
  keys.forEach(key => {
    if (!currentSchema.properties[key]) {
      delete currentSchema.properties[key];
    }
    if (
      currentSchema.properties[key] &&
      Object.keys(currentSchema.properties[key]).length > 1
    ) {
      currentSchema.properties[key] = {
        ...currentSchema.properties[key],
        readOnly: false
      };
    }
  });

  if (step === Step.BOND_OPTIONS) {
    currentSchema.properties = {
      ...currentSchema.properties,
      ...termsAndConditionsProperties()
    };
    currentSchema.required.push("agreed_to_terms_and_conditions");
  }

  return currentSchema;
};

export const getForms = ({
  step,
  formDataStart,
  setFormDataStart,
  formDataBondApplicant,
  setFormDataBondApplicant,
  setFormDataBondBusiness,
  setIsBusiness,
  formDataBondBusiness,
  formDataBondRisk,
  setFormDataBondRisk,
  formDataBondSpecific,
  setFormDataBondSpecific,
  formDataBondOptions,
  setFormDataBondOptions,
  isSkipBondOptions,
  formDataBondAgencyQuestions,
  setFormDataBondAgencyQuestions,
  formDataExternalFormSpecific,
  setFormDataExternalFormSpecific,
  formDataExternalBondOptions,
  setFormDataExternalBondOptions
}: any) => [
  {
    data: formDataBondAgencyQuestions,
    setData: setFormDataBondAgencyQuestions,
    active: step === Step.BOND_AGENCY_QUESTIONS
  },
  {
    data: formDataStart,
    setData: (data: any) => {
      setFormDataStart(data);
      setFormDataBondApplicant(data);
      setFormDataBondBusiness({
        applicant_first_name: data?.first_name,
        applicant_middle_name: data?.middle_name,
        applicant_last_name: data?.last_name,
        applicant_suffix: data?.suffix
      });
    },
    active: step === Step.START
  },
  {
    data: formDataBondApplicant,
    setData: (data: any) => {
      if (data.email) {
        data.email = data.email.toLowerCase();
      }
      setFormDataBondApplicant(data);
      setFormDataBondBusiness({
        applicant_first_name: data?.first_name,
        applicant_middle_name: data?.middle_name,
        applicant_last_name: data?.last_name,
        applicant_suffix: data?.suffix
      });
      setIsBusiness(false);
    },
    active: step === Step.BOND_APPLICANT
  },
  {
    data: formDataBondBusiness,
    setData: (data: any) => {
      if (data.email) {
        data.email = data.email.toLowerCase();
      }
      setFormDataBondBusiness(data);
      setFormDataBondApplicant({
        applicant_first_name: data?.first_name,
        applicant_middle_name: data?.middle_name,
        applicant_last_name: data?.last_name,
        applicant_suffix: data?.suffix
      });
      setIsBusiness(true);
    },
    active: step === Step.BOND_BUSINESS
  },
  {
    data: formDataBondRisk,
    setData: setFormDataBondRisk,
    active: step === Step.BOND_RISK
  },
  {
    data: formDataBondSpecific,
    setData: setFormDataBondSpecific,
    active: step === Step.BOND_SPECIFIC
  },
  {
    data: formDataBondOptions,
    setData: setFormDataBondOptions,
    active: step === Step.BOND_OPTIONS,
    isSkipBond: isSkipBondOptions
  },
  {
    data: formDataExternalFormSpecific,
    setData: setFormDataExternalFormSpecific,
    active: step === Step.EXTERNAL_BOND_SPECIFIC
  },
  {
    data: formDataExternalBondOptions,
    setData: setFormDataExternalBondOptions,
    active: step === Step.EXTERNAL_BOND_OPTIONS
  }
];

export const getSteps = ({
  formDataBondBusiness,
  formDataBondApplicant,
  setStep,
  setNeedToCheckIndemnitor,
  isStart,
  formDataBondRisk,
  formDataBondSpecific,
  isBusiness,
  step,
  formDataBondIndemnitor,
  formDataBondOptions,
  hasAgencyQuestions
}: any) => [
  {
    label: "Principal Information",
    isActive: true,
    onClick: () => {
      if (!formDataBondBusiness && !formDataBondApplicant) {
        setStep(Step.START);
        setNeedToCheckIndemnitor(true);
        return;
      }
      setStep(isBusiness ? Step.BOND_BUSINESS : Step.BOND_APPLICANT);
      setNeedToCheckIndemnitor(true);
    },
    disabled: !formDataBondBusiness && !formDataBondApplicant
  },
  {
    label: "History",
    isActive: !isStart,
    onClick: () => {
      setStep(Step.BOND_RISK);
      setNeedToCheckIndemnitor(true);
    },
    disabled: !formDataBondRisk
  },
  // {
  //   label: "Agency Specific Questions",
  //   isActive: step === Step.BOND_AGENCY_QUESTIONS, //todo CHECK WHEN BACKEND IS READY
  //   onClick: () => {
  //     console.log("new step click");
  //     // if (!formDataBondBusiness && !formDataBondApplicant) {
  //     //   setStep(Step.START);
  //     //   setNeedToCheckIndemnitor(true);
  //     //   return;
  //     // }
  //     // setStep(isBusiness ? Step.BOND_BUSINESS : Step.BOND_APPLICANT);
  //     // setNeedToCheckIndemnitor(true);
  //   },
  //   disabled: !hasAgencyQuestions
  // },
  {
    label: "Bond specific questions",
    isActive: (!isStart && step !== Step.BOND_RISK) || step === Step.BOND_AGENCY_QUESTIONS,
    onClick: () => {
      setStep(Step.BOND_SPECIFIC);
      setNeedToCheckIndemnitor(true);
    },
    disabled: !formDataBondSpecific || (!hasAgencyQuestions && step === Step.BOND_AGENCY_QUESTIONS)
  },
  {
    label: "Indemnitors",
    isActive:
      !isStart && step !== Step.BOND_RISK && step !== Step.BOND_SPECIFIC,
    onClick: () => {
      setStep(Step.BOND_INDEMNITOR);
    },
    disabled: !formDataBondIndemnitor
  },
  {
    label: "Bond Purchase",
    isActive:
      !isStart && step !== Step.BOND_RISK && step !== Step.BOND_SPECIFIC,
    onClick: () => {
      setStep(Step.PROCESSING);
    }
  },
  {
    label: "Bond Options",
    isActive:
      !isStart &&
      step !== Step.BOND_RISK &&
      step !== Step.BOND_SPECIFIC &&
      step !== Step.BOND_INDEMNITOR,
    onClick: () => {
      setStep(Step.PROCESSING);
    },
    disabled: !formDataBondOptions
  }
];

const getDataFormRecordBonds = ({
  product,
  docs,
  rate,
  apply,
  submit,
  searchParams,
  bondRecordCurrentData,
  refCode,
  isBusiness,
  bondRecord,
  formDataBondApplicant,
  formDataBondBusiness,
  formDataBondRisk,
  formDataBondSpecific,
  formDataBondIndemnitor,
  formDataBondOptions,
  formDataBondAgencyQuestions
}: any) => {
  const marketingData = getMarketingData(searchParams);

  return {
    record_id: bondRecordCurrentData?.id || null,
    ref_code: refCode,
    product_id: product?.id,
    is_business: isBusiness,
    record_salesforce_id: bondRecordCurrentData?.record_salesforce_id || null,
    record_access_key: bondRecord?.data?.access_key || "",
    form_data_by_type: {
      bond_applicant: isBusiness ? null : formDataBondApplicant,
      bond_business: isBusiness ? formDataBondBusiness : null,
      bond_risk_assessment_options: dataChecker(formDataBondRisk),
      bond_mso_custom_fields: dataChecker(formDataBondSpecific),
      bond_indemnitor: dataChecker(formDataBondIndemnitor),
      bond_options: dataChecker(formDataBondOptions),
      bond_agency_questions: dataChecker(formDataBondAgencyQuestions)
    },
    subagent_id:
      document
        ?.getElementById("bond-form-script")
        //@ts-ignore
        ?.src?.split("subagent=")[1] || null,
    partner_id: "1234",
    bond_marketing_data: marketingData,
    docs,
    rate,
    apply,
    submit
  };
};

export const getBondRequestByStep = ({
  searchParams,
  bondRecordCurrentData,
  refCode,
  isBusiness,
  bondRecord,
  formDataBondApplicant,
  formDataBondBusiness,
  formDataBondRisk,
  formDataBondSpecific,
  formDataBondIndemnitor,
  formDataBondOptions,
  formDataBondAgencyQuestions,
  product
}: any) => {
  const otherData = {
    searchParams,
    bondRecordCurrentData,
    refCode,
    isBusiness,
    bondRecord,
    formDataBondApplicant,
    formDataBondBusiness,
    formDataBondRisk,
    formDataBondSpecific,
    formDataBondIndemnitor,
    formDataBondOptions,
    formDataBondAgencyQuestions
  };
  return {
    bondApplicant: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: false,
      rate: false,
      apply: true,
      submit: false
    }),
    bondBusiness: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: false,
      rate: false,
      apply: true,
      submit: false
    }),
    bondSpecific: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: false,
      rate: true,
      apply: true,
      submit: false
    }),
    bondIndemnitor: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: false,
      rate: true,
      apply: true,
      submit: false
    }),
    bondAgencyQuestions: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: false,
      rate: false,
      apply: true,
      submit: false
    }),
    bondOptions: getDataFormRecordBonds({
      ...otherData,
      product,
      docs: true,
      rate: true,
      apply: true,
      submit: true
    })
  };
};

export const getBondInfoData = (product: any) =>
  product &&
  `${product?.state?.name} ${
    product?.bond_amount ? ` | Bond Amount: $${product?.bond_amount}` : ""
  }`;

/** uncomment when term will be fixed
 * ${
    product?.obligee?.cancellation_term_days
      ? ` | Term: ${product?.obligee?.cancellation_term_days} days`
      : ""
  }
 */

export const removeNbsp = (str: string = "") =>
  str
    .split("")
    .map((element: string, i: number) => {
      if (str.charCodeAt(i) === 160) {
        return " ";
      }
      return element;
    })
    .join("");

interface NestedObject {
  [key: string]: NestedObject | any;
}

export const deleteKeysIfNestedMatches = (
  obj: NestedObject,
  valuesToDelete: string[]
): void => {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      deleteKeysIfNestedMatches(obj[key], valuesToDelete);
    }
    if (valuesToDelete.includes(obj[key])) {
      delete obj[key];
    }
  }
};
